import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as z from 'zod'

import { UserToInviteData } from 'api/users/fetchers/inviteUserApi'
import { emptyString } from 'utils/common'

const EMAIL_MAX_CHARACTERS = 258
const EMAIL_MIN_CHARACTERS = 6
const NAME_MAX_CHARACTERS = 124
const NAME_MIN_CHARACTERS = 2

const emptyArray: never[] = []

export const getDefaultValues = (): UserToInviteData => ({
  isExternal: false,
  email: emptyString,
  lastname: emptyString,
  firstname: emptyString,
  groupsMembership: emptyArray,
})

export const useValidationScheme = (tenantDomains: string[]) => {
  const { t } = useTranslation()

  return useMemo(
    () =>
      z.object({
        email: z
          .string()
          .trim()
          .email()
          .min(
            EMAIL_MIN_CHARACTERS,
            t('os.members.add_new_member.email.min_length_message', { minCharacters: EMAIL_MIN_CHARACTERS }),
          )
          .max(
            EMAIL_MAX_CHARACTERS,
            t('os.members.add_new_member.email.max_length_message', { maxCharacters: EMAIL_MAX_CHARACTERS }),
          )
          .refine(
            email => tenantDomains.some(domain => email.endsWith(domain)),
            t('os.members.add_new_member.email.wrong_domain_message'),
          ),
        firstname: z
          .string()
          .trim()
          .min(
            NAME_MIN_CHARACTERS,
            t('os.members.add_new_member.first_name.min_length_message', { minCharacters: NAME_MIN_CHARACTERS }),
          )
          .max(
            NAME_MAX_CHARACTERS,
            t('os.members.add_new_member.first_name.max_length_message', { maxCharacters: NAME_MAX_CHARACTERS }),
          ),
        lastname: z
          .string()
          .trim()
          .min(
            NAME_MIN_CHARACTERS,
            t('os.members.add_new_member.last_name.min_length_message', { minCharacters: NAME_MIN_CHARACTERS }),
          )
          .max(
            NAME_MAX_CHARACTERS,
            t('os.members.add_new_member.last_name.max_length_message', { maxCharacters: NAME_MAX_CHARACTERS }),
          ),
        groupsMembership: z.array(z.string()).optional(),
        isExternal: z.boolean(),
      }),
    [t, tenantDomains],
  )
}

export const transformDomainsList = (domains: string[]) => {
  return domains.reduce((acc, domain, index) => {
    return acc + `@${domain}` + (index === domains.length - 1 ? '' : ', ')
  }, '')
}
